import { select, takeEvery, takeLeading } from 'redux-saga/effects';
import { routineSaga } from '@pvolve/sdk/src/app/utils/sagas';

import Actions from 'src/state/root-actions';
import * as Segment from './segment-tracker/Handlers';
import initSegmentTraker, { identify } from './segment-tracker/segmetTracker';
import { checkAnalytics } from './segment-tracker/utils';
import { LOGS } from './segment-tracker/constants';
import {
    DataPayload,
    Load,
    MarketingOptInProperties,
    OrderProperties,
    ProductProperties,
    TrialProductProperties,
} from './segment-tracker/types';

import OrderSelectors from 'src/state/orders/selectors';
import { isEmpty } from 'lodash';

const {
    segment: { load },
    web: {
        checkoutPageLoaded,
        checkoutEmailAuthLookup,
        checkoutEmailAuthComplete,
        checkoutSocialAuthTriggered,
        checkoutSocialAuthComplete,
        checkoutValidCreditCard,
        checkoutComplete,
        checkoutAddressComplete,
        checkoutShippingComplete,
    },
    account: { getAllAttrs },
} = Actions;

class SegmentSaga {
    private isTrial: boolean;
    public order_details: OrderProperties;
    public product_details: ProductProperties[];
    public trial_details: TrialProductProperties;
    public marketing_opt_in: MarketingOptInProperties;

    constructor() {
        this.isTrial = false;
        this.order_details = {};
        this.product_details = [];
        this.trial_details = {};
        this.marketing_opt_in = {
            source: '',
        };
    }

    getProducts = (items: [{ [k: string]: any }]): ProductProperties[] => {
        items.forEach(function (product: { [k: string]: any }) {
            saga.product_details.push({
                product_id: product.product_id,
                sku: product.sku,
                name: product.title,
                brand: product.vendor,
                variant: product.variant_title,
                price: product.price / 100,
                quantity: product.quantity,
                compare_at_price: product.compare_at_price
                    ? product.compare_at_price / 100
                    : undefined,
                presentment_amount: product.price / 100,
                url: product.url,
                shopify_product_id: product.product_id,
                shopify_variant_id: product.variant_id,
                image_url: product.image,
            });
        });
        return saga.product_details;
    };

    *init() {
        yield takeEvery(load.trigger, saga.load);
        yield takeLeading(getAllAttrs.success, saga.handleIdentyUser);
        yield takeEvery(checkoutPageLoaded, saga.handleCheckoutPageLoaded);
        yield takeEvery(checkoutEmailAuthLookup, saga.handleCheckoutLoginStep);
        yield takeEvery(checkoutEmailAuthComplete, saga.handleCheckoutLoginStep);
        yield takeEvery(checkoutSocialAuthTriggered, saga.handleCheckoutLoginStep);
        yield takeEvery(checkoutSocialAuthComplete, saga.handleCheckoutLoginStep);
        yield takeEvery(checkoutAddressComplete, saga.handleCheckoutAddressStep);
        yield takeEvery(checkoutShippingComplete, saga.handleCheckoutShippingStep);
        yield takeEvery(checkoutValidCreditCard, saga.handleCheckoutCreditCardValid);
        yield takeEvery(checkoutComplete, saga.handleCheckoutComplete);
    }

    load = routineSaga({
        routine: load,
        *request({ payload: { location } }: Load) {
            if (checkAnalytics()) {
                initSegmentTraker({ location });
            } else {
                throw new Error(LOGS.ERROR.load);
            }
        },
    });

    *handleIdentyUser(getAllAttrs: DataPayload) {
        if (getAllAttrs.payload.user?.user_id) {
            setTimeout(() => identify(), 100);
        }
    }

    *handleCheckoutPageLoaded(checkoutPageLoaded: DataPayload) {
        const checkout_id = yield select(OrderSelectors.draftId);
        const cart_details = checkoutPageLoaded.payload?.cart;
        const draft_order_details = checkoutPageLoaded.payload?.draft_order;

        saga.isTrial = checkoutPageLoaded?.payload?.isTrial;
        if (saga.isTrial) {
            const cart_item = cart_details?.items[0];
            saga.trial_details = {
                account_type:
                    checkoutPageLoaded.payload?.isSignup ??
                    checkoutPageLoaded.payload?.provider ??
                    undefined,
                cart_id: undefined,
                checkout_id,
                name: cart_item?.product_title,
                payment_method: 'stripe',
                subscription_price: cart_item?.price / 100,
                product_id: cart_item?.product_id,
                sku: cart_item?.sku,
                source_name: 'web',
                step: 1,
                url: document.location?.href,
            };
            Segment.handleTrialSignupStartedEvent(saga.trial_details);
            Segment.handleTrialSignupStepEvent(saga.trial_details);
        } else {
            const discount =
                cart_details?.total_discount && cart_details?.total_discount !== 0
                    ? cart_details?.total_discount / 100
                    : undefined;
            saga.order_details = {
                cart_id: cart_details?.token,
                source_name: 'web',
                checkout_id,
                discount,
                coupon: undefined,
                category: 'Custom checkout',
                presentment_currency: cart_details?.currency,
                currency: cart_details?.currency,
                products: saga.getProducts(cart_details?.items),
                subtotal: draft_order_details?.price_summary?.subtotal / 100,
                sent_from: 'Pvolve',
                step: 1,
            };
            Segment.handleCheckoutStartedEvent(saga.order_details);
            Segment.handleCheckoutStepViewedEvent(saga.order_details);
        }
    }

    *handleCheckoutLoginStep(checkoutAuthAction: DataPayload) {
        if (checkoutAuthAction.payload.error) {
            return;
        }

        const email = checkoutAuthAction.payload?.email;
        if (email) {
            saga.order_details.email = email;
            identify(email, { email });
        }

        if (saga.isTrial) {
            saga.trial_details.account_type =
                typeof checkoutAuthAction.payload?.isSignup !== 'undefined'
                    ? 'pvolve'
                    : checkoutAuthAction.payload?.provider ?? undefined;
            Segment.handleTrialSignupStepCompletedClickEvent(saga.trial_details);
        } else {
            Segment.handleCheckoutStepCompletedClickEvent(saga.order_details);
        }

        if (typeof checkoutAuthAction.payload?.optIn !== 'undefined') {
            saga.marketing_opt_in.email = checkoutAuthAction.payload?.email;
            saga.marketing_opt_in.is_email_opt_in = checkoutAuthAction.payload?.optIn;
            saga.marketing_opt_in.source = 'account creation';
            Segment.handleCheckoutMarketingOptInSubmitEvent(saga.marketing_opt_in);
        }

        if (saga.isTrial) {
            (saga.trial_details.step as number) += 1;
            Segment.handleTrialSignupStepEvent(saga.trial_details);
        } else {
            (saga.order_details.step as number) += 1;
            Segment.handleCheckoutStepViewedEvent(saga.order_details);
        }
    }

    *handleCheckoutAddressStep(checkoutAddressComplete: DataPayload) {
        if (checkoutAddressComplete.payload?.draft_order?.discount_summary) {
            const discount_summary = checkoutAddressComplete.payload.draft_order.discount_summary;
            const price_summary = checkoutAddressComplete.payload.draft_order.price_summary;
            saga.order_details.discount =
                discount_summary.total_discount === 0 ? undefined : discount_summary.total_discount;
            saga.order_details.coupon = isEmpty(discount_summary.discount.code)
                ? undefined
                : discount_summary.discount.code;
            saga.order_details.subtotal = price_summary.subtotal - price_summary.discount;
        }
        Segment.handleCheckoutStepCompletedClickEvent(saga.order_details);
        (saga.order_details.step as number) += 1;
        Segment.handleCheckoutStepViewedEvent(saga.order_details);
    }

    *handleCheckoutShippingStep(checkoutShippingComplete: DataPayload) {
        saga.order_details.shipping_method = checkoutShippingComplete.payload?.shipping_method;
        Segment.handleCheckoutStepCompletedClickEvent(saga.order_details);
        (saga.order_details.step as number) += 1;
        Segment.handleCheckoutStepViewedEvent(saga.order_details);
    }

    *handleCheckoutCreditCardValid(checkoutValidCreditCard: DataPayload) {
        if (saga.isTrial) {
            (saga.trial_details.step as number) += 1;
            Segment.handleTrialValidCreditCardEvent(saga.trial_details);
        } else {
            (saga.order_details.step as number) += 1;
            saga.order_details.payment_method = 'stripe';
            Segment.handleCheckoutPaymentInfoEnteredEvent(saga.order_details);
        }
    }

    *handleCheckoutComplete(checkoutComplete: DataPayload) {
        if (saga.isTrial) {
            Segment.handleTrialSignupStepCompletedClickEvent(saga.trial_details);
            const trial_order_properties = {
                trial_start_date: undefined,
                trial_end_date: undefined,
                coupon_name: undefined,
                coupon_id: undefined,
                discount:
                    checkoutComplete.payload?.order?.price_summary?.discount === 0
                        ? undefined
                        : checkoutComplete.payload?.order?.price_summary?.discount,
                order_id: checkoutComplete.payload?.order?.order_id,
                subscriptionId: checkoutComplete.payload?.order?.subscription_id,
                source_name: saga.trial_details.source_name,
                payment_method: saga.trial_details.payment_method,
                name: saga.trial_details.name,
                subscription_price: saga.trial_details.subscription_price,
                product_id: saga.trial_details.product_id,
                sku: saga.trial_details.sku,
            };
            Segment.handleTrialOrderCompletedEvent(trial_order_properties);
        } else {
            Segment.handleCheckoutStepCompletedClickEvent(saga.order_details);
        }
    }
}

export const saga = new SegmentSaga();
